import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { useEffect } from 'react';

import { clearUserSession } from '../../auth/sessionCleanup';
import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { useToasts } from '../../cdl/Toast/useToasts';
import { UserOnboardRequestErrorResponse } from '../../common/api/clients/userOnboard.api';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useOpenFreshchat } from '../../common/hooks/useOpenFreshchat';
import { Spinner } from '../../common/Spinner/Spinner';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { storeOnboardingUrl } from '../../common/utils/onboardingRedirect.util';
import { storeShowWelcomePopupInStorage } from '../../common/utils/showWelcomePopup.util';
import { AuthenticationPageContentBox } from '../components/AuthenticationPageContentBox';
import { FormikOnboardForm } from '../components/FormikOnboardForm';
import { useUserOnboarding } from '../hooks/useUserOnboarding';
import { useUserOnboardingUpdate } from '../hooks/useUserOnboardingUpdate';

export interface FormikRegistrationState {
    firstName: string;
    lastName: string;
    phone: string;
    emailAddress: string;
    password: string;
    newsletterEnabled: boolean;
}

const validate = (values: FormikRegistrationState) => {
    const errors: FormikErrors<FormikRegistrationState> = {};

    if (!values.firstName) {
        errors.firstName = translate('onboard.error.requiredFirstName');
    }

    if (!values.lastName) {
        errors.lastName = translate('onboard.error.requiredLastName');
    }

    if (!values.password) {
        errors.password = translate('onboard.error.requiredPassword');
    }

    return errors;
};

export const OnboardPage = () => {
    const { userId, token } = useParams({ from: '/onboard/$userId/$token' });
    const userQuery = useUserOnboarding(userId, token);
    const userOnboardingMutation = useUserOnboardingUpdate(userId, token);
    const { addErrorToast } = useToasts();
    const openFreshchat = useOpenFreshchat();
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useAuth0();

    useDocumentTitle(translate('onboard.pageTitle'));

    useEffect(() => {
        if (isAuthenticated) {
            storeOnboardingUrl(window.location.pathname);
            clearUserSession();

            logout({
                logoutParams: {
                    returnTo: window.location.origin + '/app/onboard-redirect',
                },
            });
        }
    }, [isAuthenticated, logout]);

    if (userQuery.isPending) {
        return (
            <AuthenticationPageContentBox>
                <Box style={{ textAlign: 'center' }}>
                    <Spinner color={theme.colors.foreground.default} />
                </Box>
            </AuthenticationPageContentBox>
        );
    }

    if (!userQuery.isSuccess) {
        return (
            <AuthenticationPageContentBox>
                <Text variant="title" as="h1">
                    {translate('onboard.error.invitationLinkNoLongerValid')}
                </Text>
                <Text as="p" variant="small" color="foreground.muted">
                    {translate('onboard.error.furtherSupportInformation')}
                </Text>
                <Box marginTop={24}>
                    <Button emphasis="high" type="submit" onClick={() => openFreshchat()}>
                        {translate('onboard.contactUs')}
                    </Button>
                </Box>
            </AuthenticationPageContentBox>
        );
    }

    const initialFormValues: FormikRegistrationState = {
        firstName: userQuery.data.firstname || '',
        lastName: userQuery.data.lastname || '',
        phone: userQuery.data.phone || '',
        emailAddress: userQuery.data.email,
        password: '',
        newsletterEnabled: false,
    };

    const handleSubmit = async (values: FormikRegistrationState, meta: FormikHelpers<FormikRegistrationState>) => {
        try {
            await userOnboardingMutation.mutateAsync({
                ...userQuery.data,
                password: values.password,
                phone: values.phone,
                firstname: values.firstName,
                lastname: values.lastName,
                mailSettings: {
                    ...userQuery.data.mailSettings,
                    newsletter: values.newsletterEnabled,
                },
            });

            storeShowWelcomePopupInStorage(true);

            // After successful onboarding, redirect to Auth0 login
            navigate({
                to: '/login',
                search: { email: userQuery.data.email },
            });
        } catch (error) {
            const axiosError = error as AxiosError<UserOnboardRequestErrorResponse>;

            if (axiosError.response?.status !== 400) {
                addErrorToast(translate('onboard.error.weCouldNotConnectToCloselink'));
            } else {
                switch (axiosError.response.data.errorCode) {
                    case 'MAIL_NOT_UNIQUE':
                        meta.setErrors({
                            emailAddress: translate('onboard.error.emailNotUnique'),
                        });
                        break;
                    case 'PASSWORD_SAFETY':
                        meta.setErrors({
                            password: axiosError.response.data.message || translate('onboard.error.insecurePassword'),
                        });
                        break;
                }
            }
        }
    };

    return (
        <AuthenticationPageContentBox>
            <Text variant="title" as="h1">
                {translate('onboard.completeAccount')}
            </Text>
            <Text variant="small" color="foreground.muted">
                {translate('onboard.enterInformationBelow', {
                    emailAddress: userQuery.data.email,
                })}
            </Text>
            <Formik
                initialValues={initialFormValues}
                validate={validate}
                onSubmit={handleSubmit}
                component={FormikOnboardForm}
            />
        </AuthenticationPageContentBox>
    );
};
